import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { DISTRICTS } from "@/data/static";
import { Button } from "@/components/ui/button";
import { LayoutGrid, Map as MapIcon } from "lucide-react";

/* Stylized Kerala SVG with 14 district blobs roughly placed N->S.
   Coordinates are simplified, intentional hand-drawn feel. */
const NODES = {
  Kasaragod:        { x: 110, y: 60,  size: 36 },
  Kannur:           { x: 130, y: 110, size: 44 },
  Wayanad:          { x: 195, y: 130, size: 38 },
  Kozhikode:        { x: 140, y: 165, size: 46 },
  Malappuram:       { x: 180, y: 205, size: 50 },
  Palakkad:         { x: 235, y: 230, size: 48 },
  Thrissur:         { x: 170, y: 265, size: 44 },
  Ernakulam:        { x: 165, y: 320, size: 46 },
  Idukki:           { x: 230, y: 345, size: 52 },
  Kottayam:         { x: 165, y: 380, size: 38 },
  Alappuzha:        { x: 130, y: 415, size: 36 },
  Pathanamthitta:   { x: 175, y: 445, size: 36 },
  Kollam:           { x: 130, y: 490, size: 40 },
  Thiruvananthapuram: { x: 145, y: 545, size: 44 },
};

export default function KeralaMap({ districtData = [] }) {
  const [view, setView] = useState("map"); // map | grid
  const [hover, setHover] = useState(null);
  const counts = Object.fromEntries(districtData.map(d => [d.name, d.members]));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">14 District Units</h2>
          <p className="text-muted-foreground mt-1 text-sm">Click a district to view its committee and members.</p>
        </div>
        <div className="inline-flex rounded-lg border border-border p-1 bg-card">
          <Button
            size="sm" variant={view === "map" ? "default" : "ghost"}
            onClick={() => setView("map")}
            className={view === "map" ? "bg-brand-blue text-white" : ""}
            data-testid="map-view-btn"
          >
            <MapIcon className="w-4 h-4 mr-1.5" /> Map
          </Button>
          <Button
            size="sm" variant={view === "grid" ? "default" : "ghost"}
            onClick={() => setView("grid")}
            className={view === "grid" ? "bg-brand-blue text-white" : ""}
            data-testid="grid-view-btn"
          >
            <LayoutGrid className="w-4 h-4 mr-1.5" /> Grid
          </Button>
        </div>
      </div>

      {view === "map" ? (
        <div className="grid lg:grid-cols-[1fr_340px] gap-8 items-start">
          <div className="relative rounded-2xl border border-border bg-card overflow-hidden">
            <svg viewBox="0 0 340 620" className="relative w-full h-auto" style={{ maxHeight: 620 }}>
              <image
                href={process.env.PUBLIC_URL + "/kerala-map.jpg"}
                x="0" y="0" width="340" height="620"
                preserveAspectRatio="xMidYMid meet"
                opacity="0.9"
              />
              {DISTRICTS.map((d, i) => {
                const n = NODES[d.name];
                if (!n) return null;
                const isHover = hover === d.name;
                return (
                  <motion.g
                    key={d.name}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: i * 0.04, type: "spring", stiffness: 200 }}
                    onMouseEnter={() => setHover(d.name)}
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: "pointer" }}
                  >
                    <Link to={`/district/${d.slug}`}>
                      <circle
                        cx={n.x} cy={n.y} r={n.size / 2 + 6}
                        fill={isHover ? "rgba(246,139,31,0.25)" : "rgba(13,79,181,0.15)"}
                      />
                      <circle
                        cx={n.x} cy={n.y} r={n.size / 2}
                        fill={isHover ? "#F68B1F" : "#0D4FB5"}
                        stroke="#fff" strokeWidth="2"
                        data-testid={`map-district-${d.slug}`}
                      />
                      <text
                        x={n.x} y={n.y + 4}
                        textAnchor="middle"
                        fontSize="10"
                        fontWeight="700"
                        fill="white"
                        style={{ fontFamily: "IBM Plex Mono, monospace", pointerEvents: "none" }}
                      >
                        {d.abbr}
                      </text>
                    </Link>
                  </motion.g>
                );
              })}
            </svg>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 sticky top-24">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              {hover ? "District" : "Hover or click any district"}
            </div>
            {hover ? (
              <DistrictPreview name={hover} members={counts[hover]} />
            ) : (
              <div>
                <div className="text-2xl font-black tracking-tight">Kerala</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  14 district units. Hundreds of local bodies. One technical community
                  building Digital Kerala.
                </p>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <Stat label="Districts" value="14" />
                  <Stat label="Local Bodies" value="900+" />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {DISTRICTS.map((d, i) => (
            <motion.div
              key={d.slug}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
            >
              <Link
                to={`/district/${d.slug}`}
                data-testid={`grid-district-${d.slug}`}
                className="block p-4 rounded-xl border border-border bg-card hover-lift hover:border-brand-blue/40"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="w-9 h-9 rounded-md bg-brand-blue text-white font-mono font-bold text-xs flex items-center justify-center">
                    {d.abbr}
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Unit</span>
                </div>
                <div className="font-semibold text-sm">{d.name}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {counts[d.name] != null ? `${counts[d.name]} members` : "—"}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function DistrictPreview({ name, members }) {
  const d = DISTRICTS.find(x => x.name === name);
  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-md bg-brand-blue text-white font-mono font-bold flex items-center justify-center">
          {d?.abbr}
        </div>
        <div>
          <div className="font-bold text-lg">{name}</div>
          <div className="text-xs text-muted-foreground">District Unit</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <Stat label="Members" value={members != null ? String(members) : "—"} />
        <Stat label="Local Bodies" value="—" />
      </div>
      <Link
        to={`/district/${d?.slug}`}
        className="mt-4 inline-flex w-full justify-center items-center rounded-md bg-brand-blue text-white text-sm font-medium px-4 py-2 hover:bg-brand-blue/90"
      >
        Open District Page
      </Link>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-border p-3 bg-background">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono font-bold text-lg">{value}</div>
    </div>
  );
}
